/* eslint-disable @typescript-eslint/no-non-null-assertion */
/*!
 * Copyright (c) 2015-present, Okta, Inc. and/or its affiliates. All rights reserved.
 * The Okta software accompanied by this notice is provided pursuant to the Apache License, Version 2.0 (the "License.")
 *
 * You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0.
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *
 * See the License for the specific language governing permissions and limitations under the License.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable max-statements, complexity, max-depth */
import { interact } from './interact';
import { introspect } from './introspect';
import { remediate } from './remediate';
import { getFlowSpecification } from './flow';
import { IdxStatus, } from '../types';
import { isIdxResponse } from './types/idx-js';
import { getSavedTransactionMeta, saveTransactionMeta } from './transactionMeta';
import { getAvailableSteps, getEnabledFeatures, getMessagesFromResponse, isTerminalResponse } from './util';
function initializeValues(options) {
    // remove known options, everything else is assumed to be a value
    const knownOptions = ['flow', 'remediators', 'actions', 'withCredentials', 'step'];
    const values = Object.assign({}, options);
    knownOptions.forEach(option => {
        delete values[option];
    });
    return values;
}
function initializeData(authClient, data) {
    const { options } = data;
    let { flow, withCredentials, remediators, actions } = options;
    const status = IdxStatus.PENDING;
    // certain options can be set by the flow specification
    flow = flow || authClient.idx.getFlow() || 'default';
    if (flow) {
        authClient.idx.setFlow(flow);
        const flowSpec = getFlowSpecification(authClient, flow);
        // Favor option values over flow spec
        withCredentials = (typeof withCredentials !== 'undefined') ? withCredentials : flowSpec.withCredentials;
        remediators = remediators || flowSpec.remediators;
        actions = actions || flowSpec.actions;
    }
    return Object.assign(Object.assign({}, data), { options: Object.assign(Object.assign({}, options), { flow, withCredentials, remediators, actions }), status });
}
function getDataFromIntrospect(authClient, data) {
    return __awaiter(this, void 0, void 0, function* () {
        const { options } = data;
        const { stateHandle, withCredentials, version, state, scopes, recoveryToken, activationToken } = options;
        let idxResponse;
        let meta;
        if (stateHandle) {
            idxResponse = yield introspect(authClient, { withCredentials, version, stateHandle });
        }
        else {
            // Try to resume saved transaction
            meta = getSavedTransactionMeta(authClient, { state, recoveryToken, activationToken });
            let interactionHandle = meta === null || meta === void 0 ? void 0 : meta.interactionHandle; // may be undefined
            if (!interactionHandle) {
                // start a new transaction
                authClient.transactionManager.clear();
                const interactResponse = yield interact(authClient, {
                    withCredentials,
                    state,
                    scopes,
                    activationToken,
                    recoveryToken
                });
                interactionHandle = interactResponse.interactionHandle;
                meta = interactResponse.meta;
            }
            // Introspect to get idx response
            idxResponse = yield introspect(authClient, { withCredentials, version, interactionHandle });
        }
        return Object.assign(Object.assign({}, data), { idxResponse, meta });
    });
}
function getDataFromRemediate(data) {
    return __awaiter(this, void 0, void 0, function* () {
        let { idxResponse, options, values } = data;
        const { autoRemediate, remediators, actions, flow, step, } = options;
        const shouldRemediate = (autoRemediate !== false && (remediators || actions || step));
        if (!shouldRemediate) {
            return data;
        }
        values = Object.assign(Object.assign({}, values), { stateHandle: idxResponse.rawIdxState.stateHandle });
        // Can we handle the remediations?
        const { idxResponse: idxResponseFromRemediation, nextStep, canceled, } = yield remediate(idxResponse, values, { remediators, actions, flow, step });
        idxResponse = idxResponseFromRemediation;
        return Object.assign(Object.assign({}, data), { idxResponse, nextStep, canceled });
    });
}
function getTokens(authClient, data) {
    return __awaiter(this, void 0, void 0, function* () {
        let { meta, idxResponse } = data;
        const { interactionCode } = idxResponse;
        const { clientId, codeVerifier, ignoreSignature, redirectUri, urls, scopes, } = meta;
        const tokenResponse = yield authClient.token.exchangeCodeForTokens({
            interactionCode,
            clientId,
            codeVerifier,
            ignoreSignature,
            redirectUri,
            scopes
        }, urls);
        return tokenResponse.tokens;
    });
}
function finalizeData(authClient, data) {
    return __awaiter(this, void 0, void 0, function* () {
        let { options, idxResponse, canceled, status, } = data;
        const { exchangeCodeForTokens } = options;
        let shouldClearTransaction = false;
        let clearSharedStorage = true;
        let interactionCode;
        let tokens;
        let enabledFeatures;
        let availableSteps;
        let messages;
        let terminal;
        if (idxResponse) {
            enabledFeatures = getEnabledFeatures(idxResponse);
            availableSteps = getAvailableSteps(idxResponse);
            messages = getMessagesFromResponse(idxResponse);
            terminal = isTerminalResponse(idxResponse);
        }
        if (terminal) {
            status = IdxStatus.TERMINAL;
            shouldClearTransaction = true;
            clearSharedStorage = false;
        }
        else if (canceled) {
            status = IdxStatus.CANCELED;
            shouldClearTransaction = true;
        }
        else if (idxResponse === null || idxResponse === void 0 ? void 0 : idxResponse.interactionCode) {
            interactionCode = idxResponse.interactionCode;
            if (exchangeCodeForTokens === false) {
                status = IdxStatus.SUCCESS;
                shouldClearTransaction = false;
            }
            else {
                tokens = yield getTokens(authClient, data);
                status = IdxStatus.SUCCESS;
                shouldClearTransaction = true;
            }
        }
        return Object.assign(Object.assign({}, data), { status,
            interactionCode,
            tokens,
            shouldClearTransaction,
            clearSharedStorage,
            enabledFeatures,
            availableSteps,
            messages,
            terminal });
    });
}
function handleError(err, data) {
    let { error, status, shouldClearTransaction } = data;
    // current version of idx-js will throw/reject IDX responses. Handle these differently than regular errors
    if (isIdxResponse(err)) {
        error = err;
        status = IdxStatus.FAILURE;
        shouldClearTransaction = true;
    }
    else {
        // error is not an IDX response, throw it like a regular error
        throw err;
    }
    return Object.assign(Object.assign({}, data), { error, status, shouldClearTransaction });
}
export function run(authClient, options = {}) {
    return __awaiter(this, void 0, void 0, function* () {
        let data = {
            options,
            values: initializeValues(options)
        };
        data = initializeData(authClient, data);
        try {
            data = yield getDataFromIntrospect(authClient, data);
            data = yield getDataFromRemediate(data);
        }
        catch (err) {
            data = handleError(err, data);
        }
        data = yield finalizeData(authClient, data);
        const { idxResponse, meta, shouldClearTransaction, clearSharedStorage, status, enabledFeatures, availableSteps, tokens, nextStep, messages, error, interactionCode } = data;
        if (shouldClearTransaction) {
            authClient.transactionManager.clear({ clearSharedStorage });
        }
        else {
            // ensures state is saved to sessionStorage
            saveTransactionMeta(authClient, Object.assign({}, meta));
            if (idxResponse) {
                // Save intermediate idx response in storage to reduce introspect call
                const { rawIdxState: rawIdxResponse, requestDidSucceed } = idxResponse;
                authClient.transactionManager.saveIdxResponse({
                    rawIdxResponse,
                    requestDidSucceed
                });
            }
        }
        // from idx-js, used by the widget
        const { actions, context, neededToProceed, proceed, rawIdxState } = idxResponse || {};
        return Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({ status: status }, (meta && { meta })), (enabledFeatures && { enabledFeatures })), (availableSteps && { availableSteps })), (tokens && { tokens })), (nextStep && { nextStep })), (messages && messages.length && { messages })), (error && { error })), { interactionCode, 
            // from idx-js
            actions: actions, context: context, neededToProceed: neededToProceed, proceed: proceed, rawIdxState: rawIdxState });
    });
}
